// src/hooks/socketService.tsx - DROP-IN REPLACEMENT
import { io, Socket } from "socket.io-client";
import { SOCKET_API_URL } from "../config/config";
import logger, { Logger } from '../utils/logger';

interface ServerToClientEvents {
  message: (data: {
    createdAt: string;
    message: {
      from: string;
      id: string;
      timestamp: string;
      text: { body: string };
      type: string;
    };
    username: string;
  }) => void;
  roomData: (data: {
    room: string;
    users: { id: string; username: string; room: string }[];
  }) => void;
}

interface ClientToServerEvents {
  join: (
    options: { username: string; room: string },
    callback: (error?: string) => void
  ) => void;
  sendMessage: (message: string, callback: () => void) => void;
  "leave-room": (callback: () => void) => void;
}

// **SOLUTION: Lazy initialization - no immediate connection**
let socket: Socket<any> | null = null;
let isConnecting = false;

// **Function to get socket only when needed**
const getSocket = (): Socket<any> => {
  if (socket) {
    return socket;
  }

  if (!socket) {
    logger.log("🔌 Creating socket connection (lazy initialization)");

    const token = localStorage.getItem("auth_token");
    Logger.log(">>>", token?.split(" ")[1]);

    socket = io(SOCKET_API_URL, {
      reconnectionDelayMax: 10000,
      autoConnect: false, // **KEY FIX: Prevent auto-connection**
      query: {
        path: "/",
        token: token?.split(" ")[1] || "",
      },
      transports: ["websocket"],
    });

    socket.on("connect", () => {
      logger.log("🔌 Socket connected (lazy)");
      isConnecting = false;
      Logger.log("Connected to Socket.IO server");
    });

    socket.on("connect_error", (error) => {
      console.error("Connection Error: ", error);
      isConnecting = false;
    });

    socket.io.on("error", (error) => {
      Logger.log("Socket error: ", error);
      isConnecting = false;
    });

    socket.on("disconnect", (reason) => {
      console.error("Disconnected: ", reason);
      isConnecting = false;
    });
  }

  return socket;
};

const disconnectRoom = () => {
  const socketInstance = socket; // Use existing socket if available
  if (socketInstance && socketInstance.connected) {
    socketInstance.emit("leave-room", (error?: string) => {
      Logger.log("Disconnect Error: ", error);
      if (error) {
        console.error("Disconnect Error: ", error);
        alert(error);
      }
    });
  }
};

const joinRoom = (username: string, room: string): void => {
  logger.log("🔌 joinRoom() called with:", { username, room });
  const socketInstance = getSocket(); // **This triggers lazy connection**
  logger.log("🔌 joinRoom() - socket instance obtained, connecting...");
  socketInstance.connect(); // **Connect only when joining room**
  socketInstance.emit("join", { username, room }, (error?: string) => {
    if (error) {
      console.error("Join Error: ", error);
      alert(error);
    } else {
      logger.log("🔌 joinRoom() - Successfully joined room:", room);
    }
  });
};

const sendMessageFun = (message: string): void => {
  const socketInstance = getSocket(); // **This triggers lazy connection**
  socketInstance.emit("sendMessage", message, () => {
    Logger.log("Message sent: ", message);
  });
};

const onMessage = (
  callback: (data: {
    createdAt: string;
    message: {
      from: string;
      id: string;
      timestamp: string;
      text: { body: string };
      type: string;
    };
    username: string;
  }) => void
): void => {
  // **Don't create socket just for setting up listeners**
  if (!socket) {
    logger.log("🔌 onMessage: No socket yet, storing callback for later");
    return;
  }
  socket.on("message", callback);
};

// **Create a proxy object that maintains the same interface**
const socketProxy = {
  get connected() {
    return socket?.connected || false;
  },
  on: (event: string, callback: any) => {
    // **CRITICAL: Don't create socket during middleware setup**
    logger.log("🔌 socketProxy.on() called for event:", event);
    if (!socket) {
      logger.log("🔌 No socket exists, listener will be set up later");
      return;
    }
    return socket.on(event, callback);
  },
  emit: (event: string, ...args: any[]) => {
    const socketInstance = getSocket();
    if (!socketInstance.connected) {
      socketInstance.connect();
    }
    return socketInstance.emit(event, ...args);
  },
  disconnect: () => {
    if (socket) {
      return socket.disconnect();
    }
  }
};

export { 
  socketProxy as socket, 
  joinRoom, 
  sendMessageFun, 
  onMessage, 
  disconnectRoom 
};

logger.log("🔌 SocketService loaded - NO auto-connection (lazy initialization)");